import HistoryIcon from '@mui/icons-material/History';
import dayjs from 'dayjs';
import { useState } from 'react';
import { Fragment } from 'react/jsx-runtime';
import { ModalStyled, TooltipStyled } from 'src/components';
import { useCurrentUser } from 'src/hooks';
import { CartApprovalDetails } from 'src/types';

/**
 * PLEASE NOTE: If the user was the requested approver but another admin approved their layer before they had a chance to approve it,
 * it will still show in the original requester's history as 'Sent for Approval' even though they didn't approve it,
 * This is an outlier and should be fixed in the future.
 */
export const CartApprovalHistory = ({
  cartApprovalDetails,
}: {
  cartApprovalDetails: CartApprovalDetails;
}) => {
  const { user } = useCurrentUser();
  const [showApprovalHistory, setShowApprovalHistory] = useState(false);

  if (!cartApprovalDetails || cartApprovalDetails.length === 0) {
    return null;
  }

  const { namedApprovers } = user?.subscription.orgSettings || {};

  return (
    <>
      <TooltipStyled title='Click to see approval history'>
        <button
          onClick={() => setShowApprovalHistory(!showApprovalHistory)}
          className='fill-primary-dark hover:fill-primary-dark/60'
        >
          <HistoryIcon
            className='relative h-6 min-w-[24px] cursor-pointer transition-all'
            style={{ fill: 'inherit' }}
          />
        </button>
      </TooltipStyled>
      <ModalStyled
        open={showApprovalHistory}
        onClose={() => setShowApprovalHistory(false)}
      >
        <div className='flex w-full flex-col items-center justify-center gap-4 p-4 sm:py-6 md:gap-6 md:px-6 md:py-8'>
          <h4 className='heading-300 text-primary-dark'>Approval History</h4>
          <ul className='flex w-fit flex-col items-start justify-center gap-2 px-4'>
            {cartApprovalDetails.map((detail, i) => {
              const layerIsPending = detail.status === 'pending';
              // we always show the initial request. We also show the request if the layer is pending.
              const showRequestedBy =
                layerIsPending ||
                detail.status === 'rejected' ||
                (i === 0 && !layerIsPending);

              const dateRequestedAt = detail.requestedAt
                ? dayjs(detail.requestedAt).format('D MMM YYYY h:mm A')
                : '';
              const requestedByEmail = detail.requestedBy.email;
              const requestedApprover =
                namedApprovers &&
                (detail.requestedApprover?.email ||
                  detail.requestedApprover?.name)
                  ? detail.requestedApprover?.email
                    ? detail.requestedApprover.email
                    : detail.requestedApprover.name
                  : '';

              const dateActionedAt = detail.actionedAt
                ? dayjs(detail.actionedAt).format('D MMM YYYY h:mm A')
                : '';
              const actionedByEmail = detail.actionedBy?.email || '';

              return (
                <Fragment key={detail.approvalLayer}>
                  {showRequestedBy && (
                    <li className='text-100'>
                      {dateRequestedAt}:{' '}
                      <a
                        className='text-primary underline transition-all hover:text-primary-hover'
                        href={`mailto:${requestedByEmail}`}
                      >
                        {requestedByEmail}
                      </a>{' '}
                      requested approval
                      {requestedApprover ? (
                        <>
                          {' from '}
                          {requestedApprover.includes('@') ? (
                            <a
                              className='text-primary underline transition-all hover:text-primary-hover'
                              href={`mailto:${requestedApprover}`}
                            >
                              {requestedApprover}
                            </a>
                          ) : (
                            requestedApprover
                          )}
                        </>
                      ) : null}
                      .
                    </li>
                  )}
                  {!layerIsPending && (
                    <li className='text-100'>
                      <span>
                        {dateActionedAt}:{' '}
                        <a
                          className='text-primary underline transition-all hover:text-primary-hover'
                          href={`mailto:${actionedByEmail}`}
                        >
                          {actionedByEmail}
                        </a>{' '}
                        {detail.status === 'approved' ? 'approved' : 'rejected'}{' '}
                        this cart.
                      </span>
                    </li>
                  )}
                </Fragment>
              );
            })}
          </ul>
        </div>
      </ModalStyled>
    </>
  );
};
